'use client';

import React, { useState, useEffect } from 'react';
import { EmptyState } from 'src/components/PulseUI';
import { useGetCompanySettingsQuery, useGetCodeByNameQuery } from 'src/api/redux';
import { useAuth } from 'src/context/auth';
import { useAnalytics } from 'src/hooks/analytics';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import { AspAdminItem } from './components';

type AspValue = { code: string; value: number | string };

interface AspAdminProps {
  aspValues?: AspValue[];
  setAspValues?: (
    values: AspValue[] | ((prev: AspValue[]) => AspValue[])
  ) => void;
}

const AspAdmin = ({ aspValues = [], setAspValues }: AspAdminProps) => {
  const { user } = useAuth();
  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });

  const drugPrefixes = ['drug'];
  const { selectedCodeSet = [] } = useAnalytics(ANALYTICS_LOCATION.ASP) || {};

  const codeIds = selectedCodeSet?.[0]?.detailed_filters?.[0]?.values || [];
  const { data: codeData } = useGetCodeByNameQuery(
    { codeIds },
    { skip: codeIds.length === 0 }
  );

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Load ASP values from company settings when code set changes
  useEffect(() => {
    if (companySettings?.results && selectedCodeSet.length > 0) {
      const aspValuesFromSettings = companySettings.results.find(
        (setting) => setting.code === 'asp_values'
      );

      if (aspValuesFromSettings && aspValuesFromSettings.value) {
        try {
          // Parse the JSON string to get the array of ASP values
          const parsedValues =
            typeof aspValuesFromSettings.value === 'string'
              ? JSON.parse(aspValuesFromSettings.value)
              : aspValuesFromSettings.value;

          if (Array.isArray(parsedValues)) {
            setAspValues?.(parsedValues);
          } else {
            setAspValues?.([]);
          }
        } catch (error) {
          console.error('Failed to parse ASP values:', error);
          setAspValues?.([]);
        }
      } else {
        setAspValues?.([]);
      }
    }
  }, [selectedCodeSet, companySettings, setAspValues]);

  const codeSet = selectedCodeSet[0];
  const detailedValues =
    codeSet?.detailed_filters?.flatMap((filter) => filter.detailed_values) ||
    [];

  const isDrugCode = (value: string) => {
    const lowerValue = value.toLowerCase();
    return drugPrefixes.some((prefix) => lowerValue.startsWith(prefix));
  };

  const getCodeDescription = (codeName: string) => {
    return codeData?.results?.find((code) => code.code_name === codeName)
      ?.description;
  };

  const handleSetAspValue = (code: string, value: string) => {
    // Strip out dollar signs, spaces, and commas to get the raw number
    const cleanedValue = value.replace(/[$\s,]/g, '');

    // Allow empty string to clear the field
    if (cleanedValue === '') {
      setAspValues?.((prev) => {
        const filtered = prev.filter((item) => item.code !== code);
        return filtered;
      });
      setErrors((prev) => ({
        ...prev,
        [code]: undefined,
      }));
      return;
    }

    // Validate that the value is a valid number
    if (isNaN(Number(cleanedValue))) {
      setErrors((prev) => ({
        ...prev,
        [code]: 'Please enter a valid number',
      }));
      return;
    }

    // Valid number - update value and clear any errors
    setAspValues?.((prev) => {
      const existingIndex = prev.findIndex((item) => item.code === code);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { code, value: cleanedValue };
        return updated;
      } else {
        // Add new
        return [...prev, { code, value: cleanedValue }];
      }
    });
    setErrors((prev) => ({
      ...prev,
      [code]: undefined,
    }));
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="flex flex-col gap-2">
        <div className="self-stretch justify-start text-primary text-base font-semibold font-['Inter'] leading-6">
          {selectedCodeSet?.[0]?.label}
        </div>
        <div className="self-stretch justify-start">
          <span className="text-secondary text-sm font-normal font-['Inter'] leading-5">
            Enter the ASP (average selling price) for each procedure code to
            calculate opportunity size.{' '}
            <span className="text-primary text-sm font-normal font-['Inter'] underline leading-5 pr-1">
              Note:
            </span>
            Only procedure code sets (CPT, HCPCS, ICD10P) are supported.
          </span>
        </div>
      </div>

      {detailedValues.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            title="No Code Set Selected"
            description="Please select a code set from the dropdown above to view and edit ASP values for individual codes."
            className="border-none shadow-none"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <div className="flex flex-col gap-4 overflow-y-auto">
            {detailedValues.map((item, index) => {
              const isDrug = isDrugCode(item.value);
              const aspValue = aspValues.find((asp) => asp.code === item.value);
              const description = getCodeDescription(item.value);

              return (
                <AspAdminItem
                  key={`${item.value}-${index}`}
                  codeName={item.value}
                  description={description}
                  aspValue={aspValue?.value}
                  isDrug={isDrug}
                  error={errors[item.value]}
                  onAspValueChange={handleSetAspValue}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AspAdmin;
